import { Field, Form, Formik } from "formik";
import _merge from "lodash/merge";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Button from "components/lib/Shared/Button";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
} from "components/lib/Shared/Icons/sflIcons";
import SectionDescription from "components/lib/Shared/SectionDescription";
import ValidationError from "components/lib/Shared/ValidationError";
import ElectronicAddress from "components/PLCTABS/CompanyOfficialsTab/AddressParticularsComponents/ElectronicAddress";
import PostalAddressFormForeign from "components/PLCTABS/CompanyOfficialsTab/PostalComponents/PostalAddressFormForeign";
import PostalAddressFormLocal from "components/PLCTABS/CompanyOfficialsTab/PostalComponents/PostalAddressFormLocal";
import PostalCodeForm from "components/PLCTABS/CompanyOfficialsTab/PostalComponents/PostalCodeForm";
import { Constants } from "config/constants";
import { updateProfileState } from "state/slices/profileUpdate";
import residentialDetailsValidation from "./validations/residentialDetailsValidation";
import ResidentialAddressDetailsFormKeCiFoRe from "components/PLCTABS/CompanyOfficialsTab/AddressParticularsComponents/ResidentialAddressComponents/ResidentialAddressDetailsFormKeCiFoRe";
import ResidentialAddressDetailsFormFoMi from "components/PLCTABS/CompanyOfficialsTab/AddressParticularsComponents/ResidentialAddressComponents/ResidentialAddressDetailsFormFoMi";

const ResidentialDetails = ({
  handleBackBtn,
  handleNextBtn,
  isCoApplicant = false,
}) => {
  const [validationSchema, setValidationSchema] = useState(null);
  const [initialValues, setInitialValues] = useState({});
  const { transformedComments } = useSelector(
    (state) => state.commentsSlice.profileComments
  );
  const { idType, isNonResident, postalAddress, electronicAddress, email, residentialAddressDetails } = useSelector((state) => state.profileUpdateSlice);
  const dispatch = useDispatch();

  const handleSubmit = (formData) => {
    dispatch(updateProfileState(formData));
    handleNextBtn();
  };

  useEffect(() => {
    const { schemaInitialValues, schema } = residentialDetailsValidation({
      idType,
      isCoApplicant,
    });
    const updatedElectronicAddress = {
      ...electronicAddress,
      emailAddress: electronicAddress?.emailAddress || email,
    };
    const { resolvedFields, ...restPostalAddress } = postalAddress;
    setValidationSchema(schema);
    setInitialValues(
      _merge(schemaInitialValues, {
        isNonResident: "No",
        postalAddress: {
          ...restPostalAddress,
        },
        electronicAddress: updatedElectronicAddress,
        residentialAddressDetails,
      })
    );
  }, [idType, isNonResident, postalAddress, electronicAddress]);

  return (
    <Formik
      enableReinitialize
      initialValues={{
        ...initialValues,
        comments: transformedComments,
      }}
      validationSchema={validationSchema}
      onSubmit={(values) => handleSubmit(values)}
    >
      {({ values, errors, touched }) => (
        <Form>
          <SectionDescription
            id="Step: 3 of 5"
            title="Residential Details"
            description="Please provide the correct details. The information provided will be used to process your application."
          />
          {idType === Constants.ID_TYPES.KENYAN_CITIZEN && (
            <div className="mt-4 bg-white rounded-sm shadow-sm sm:rounded md:rounded-md">
              <div className="px-4 py-6 space-y-2 sm:p-6">
                <span>Is this Official Non-Resident?</span>
                <div className="flex space-x-2">
                  <div className="flex items-center space-x-2">
                    <label
                      htmlFor="yesResident"
                      className="block text-sm font-medium text-gray-700"
                    >
                      YES
                    </label>
                    <Field
                      type="radio"
                      name="isNonResident"
                      id="yesResident"
                      value="Yes"
                    />
                  </div>
                  <div className="flex items-center space-x-2">
                    <label
                      htmlFor="notResident"
                      className="block text-sm font-medium text-gray-700"
                    >
                      NO
                    </label>
                    <Field
                      type="radio"
                      name="isNonResident"
                      id="notResident"
                      value="No"
                    />
                  </div>
                </div>
                <ValidationError
                  errors={errors}
                  touched={touched}
                  name="isNonResident"
                />
              </div>
            </div>
          )}

          {(((idType === "Kenyan Citizen" || idType === "Minor") &&
            isNonResident === "No") ||
            idType === "Foreign Resident") && (
              <>
                <div className="px-4 mt-4 space-y-4 bg-white rounded-sm shadow-sm sm:rounded md:rounded-md sm:p-6">
                  <Field
                    name="postalAddress"
                    component={PostalAddressFormLocal}
                    idType={idType}
                  />
                  <Field name="postalAddress" component={PostalCodeForm} />
                </div>
                <Field
                  name="residentialAddressDetails"
                  component={ResidentialAddressDetailsFormKeCiFoRe}
                  colWidth={4}
                />
              </>
            )}
          {(idType === "Foreigner" ||
            ((idType === "Kenyan Citizen" || idType === "Minor") &&
              isNonResident === "Yes")) && (
              <>
                <div className="px-4 mt-4 space-y-4 bg-white rounded-sm shadow-sm sm:rounded md:rounded-md sm:p-6">
                  <Field
                    name="postalAddress"
                    component={PostalAddressFormForeign}
                    idType={idType}
                  />
                </div>
                <Field
                  name="residentialAddressDetails"
                  component={ResidentialAddressDetailsFormFoMi}
                />
              </>
            )}

          <div className="flex items-center justify-between mt-6">
            <Button
              variant="backBtn"
              onClick={handleBackBtn}
              preIcon={ChevronLeftIcon}
            >
              Back to previous step
            </Button>
            <Button type="submit" postIcon={ChevronRightIcon}>
              Save and proceed
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default ResidentialDetails;
